import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  signal,
  viewChildren,
} from '@angular/core';
import { contentLayout8Style } from '@components/content/components/content-layout-8/config';
import { ContentLayout8, ContentLayout8Style } from '@components/content/components/content-layout-8/model';
import { ResponsiveService } from '@services/responsive';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { Color } from '@shared/enums';
import { Image, Link } from '@shared/models';
import { ColorPipe } from '@shared/pipes';

@Component({
  selector: 'app-content-layout-8',
  imports: [ColorPipe, TitleComponent, ButtonComponent, ImageComponent],
  templateUrl: './content-layout-8.component.html',
  styleUrl: './content-layout-8.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentLayout8Component implements OnInit {
  readonly blockElements = viewChildren('blockElements', { read: ElementRef });
  readonly data = input.required<ContentLayout8>();
  activatedImg = signal(0);
  title = signal<Title>(null!);
  items = signal<
    {
      titles: Title[];
      text: string;
      image?: Image;
      cta?: Link;
    }[]
  >([]);
  style = signal<ContentLayout8Style>(null!);
  breakpoints = inject(ResponsiveService).breakpoints;
  #window = inject(DOCUMENT).defaultView as Window;
  private offsetGap = signal(680);

  @HostListener('window:scroll') onScrollEvent() {
    const blockElements = this.blockElements();
    if (blockElements && blockElements.length && this.breakpoints() === 'desktop') {
      blockElements.forEach((blockElement, i) => {
        const el = blockElement.nativeElement as HTMLElement;
        if (
          this.#window.scrollY >= el.offsetTop - this.offsetGap() &&
          this.#window.scrollY <= el.offsetTop + el.offsetHeight - this.offsetGap()
        ) {
          this.activatedImg.set(i);
        }
      });
    }
  }

  ngOnInit(): void {
    this.style.set(contentLayout8Style[this.data().theme]!);
    const data = this.data();
    if (data.title) {
      this.title.set({
        level: 'H2',
        type: 'NORMAL',
        color: this.getTitleColor('title'),
        text: data.title,
        align: {
          mobile: 'LEFT',
          desktop: 'CENTER',
        },
        size: {
          mobile: 'H2',
          desktop: 'H2',
        },
      });
    }
    this.items.set(
      data.items.map((item) => ({
        ...item,
        titles: [
          {
            level: 'SPAN',
            type: 'SLASH',
            size: {
              mobile: 'SPAN',
              desktop: 'SPAN',
            },
            color: this.getTitleColor('itemTitle'),
            align: {
              mobile: 'LEFT',
              desktop: 'LEFT',
            },
            weight: 500,
            text: item.title,
          },
          {
            level: 'H2',
            size: {
              mobile: 'H4',
              desktop: 'H5',
            },
            font: 'INTER',
            space: '4rem',
            color: this.getTitleColor('itemSubtitle'),
            align: {
              mobile: 'LEFT',
              desktop: 'LEFT',
            },
            text: item.subtitle,
          },
        ],
      }))
    );
  }

  private getTitleColor(titleType: 'title' | 'itemTitle' | 'itemSubtitle'): keyof typeof Color {
    if (titleType === 'title' || titleType === 'itemSubtitle') {
      if (this.data().theme === 'CREAM' || this.data().theme === 'OPAL') {
        return 'BLACK';
      }
      return 'WHITE';
    }
    // itemTitle
    if (this.data().theme === 'CREAM' || this.data().theme === 'OPAL') {
      return 'GREY_LIGHT';
    }
    return 'GREY_ALPHA';
  }
}
